import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { SEO } from "../components/SEO";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Globe,
  Search,
  ChevronRight,
  MapPin,
  Languages,
} from "lucide-react";
import { getCountryPhotoByCode } from "../utils/countryPhotos";

// All world countries data with capital and language
const ALL_COUNTRIES = [
  { code: "AF", name: "Afghanistan", capital: "Kabul", language: "Pashto, Dari" },
  { code: "AL", name: "Albania", capital: "Tirana", language: "Albanian" },
  { code: "DZ", name: "Algeria", capital: "Algiers", language: "Arabic" },
  { code: "AD", name: "Andorra", capital: "Andorra la Vella", language: "Catalan" },
  { code: "AO", name: "Angola", capital: "Luanda", language: "Portuguese" },
  { code: "AG", name: "Antigua and Barbuda", capital: "St. John's", language: "English" },
  { code: "AR", name: "Argentina", capital: "Buenos Aires", language: "Spanish" },
  { code: "AM", name: "Armenia", capital: "Yerevan", language: "Armenian" },
  { code: "AU", name: "Australia", capital: "Canberra", language: "English" },
  { code: "AT", name: "Austria", capital: "Vienna", language: "German" },
  { code: "AZ", name: "Azerbaijan", capital: "Baku", language: "Azerbaijani" },
  { code: "BS", name: "Bahamas", capital: "Nassau", language: "English" },
  { code: "BH", name: "Bahrain", capital: "Manama", language: "Arabic" },
  { code: "BD", name: "Bangladesh", capital: "Dhaka", language: "Bengali" },
  { code: "BB", name: "Barbados", capital: "Bridgetown", language: "English" },
  { code: "BY", name: "Belarus", capital: "Minsk", language: "Belarusian, Russian" },
  { code: "BE", name: "Belgium", capital: "Brussels", language: "Dutch, French, German" },
  { code: "BZ", name: "Belize", capital: "Belmopan", language: "English" },
  { code: "BJ", name: "Benin", capital: "Porto-Novo", language: "French" },
  { code: "BT", name: "Bhutan", capital: "Thimphu", language: "Dzongkha" },
  { code: "BO", name: "Bolivia", capital: "Sucre", language: "Spanish" },
  { code: "BA", name: "Bosnia and Herzegovina", capital: "Sarajevo", language: "Bosnian, Croatian, Serbian" },
  { code: "BW", name: "Botswana", capital: "Gaborone", language: "English, Setswana" },
  { code: "BR", name: "Brazil", capital: "Brasilia", language: "Portuguese" },
  { code: "BN", name: "Brunei", capital: "Bandar Seri Begawan", language: "Malay" },
  { code: "BG", name: "Bulgaria", capital: "Sofia", language: "Bulgarian" },
  { code: "BF", name: "Burkina Faso", capital: "Ouagadougou", language: "French" },
  { code: "BI", name: "Burundi", capital: "Gitega", language: "Kirundi, French" },
  { code: "CV", name: "Cabo Verde", capital: "Praia", language: "Portuguese" },
  { code: "KH", name: "Cambodia", capital: "Phnom Penh", language: "Khmer" },
  { code: "CM", name: "Cameroon", capital: "Yaoundé", language: "French, English" },
  { code: "CA", name: "Canada", capital: "Ottawa", language: "English, French" },
  { code: "CF", name: "Central African Republic", capital: "Bangui", language: "French, Sango" },
  { code: "TD", name: "Chad", capital: "N'Djamena", language: "French, Arabic" },
  { code: "CL", name: "Chile", capital: "Santiago", language: "Spanish" },
  { code: "CN", name: "China", capital: "Beijing", language: "Mandarin" },
  { code: "CO", name: "Colombia", capital: "Bogotá", language: "Spanish" },
  { code: "KM", name: "Comoros", capital: "Moroni", language: "Arabic, French, Comorian" },
  { code: "CG", name: "Congo", capital: "Brazzaville", language: "French" },
  { code: "CR", name: "Costa Rica", capital: "San José", language: "Spanish" },
  { code: "HR", name: "Croatia", capital: "Zagreb", language: "Croatian" },
  { code: "CU", name: "Cuba", capital: "Havana", language: "Spanish" },
  { code: "CY", name: "Cyprus", capital: "Nicosia", language: "Greek, Turkish" },
  { code: "CZ", name: "Czech Republic", capital: "Prague", language: "Czech" },
  { code: "DK", name: "Denmark", capital: "Copenhagen", language: "Danish" },
  { code: "DJ", name: "Djibouti", capital: "Djibouti", language: "French, Arabic" },
  { code: "DM", name: "Dominica", capital: "Roseau", language: "English" },
  { code: "DO", name: "Dominican Republic", capital: "Santo Domingo", language: "Spanish" },
  { code: "EC", name: "Ecuador", capital: "Quito", language: "Spanish" },
  { code: "EG", name: "Egypt", capital: "Cairo", language: "Arabic" },
  { code: "SV", name: "El Salvador", capital: "San Salvador", language: "Spanish" },
  { code: "GQ", name: "Equatorial Guinea", capital: "Malabo", language: "Spanish, French, Portuguese" },
  { code: "ER", name: "Eritrea", capital: "Asmara", language: "Tigrinya, Arabic, English" },
  { code: "EE", name: "Estonia", capital: "Tallinn", language: "Estonian" },
  { code: "SZ", name: "Eswatini", capital: "Mbabane", language: "Swazi, English" },
  { code: "ET", name: "Ethiopia", capital: "Addis Ababa", language: "Amharic" },
  { code: "FJ", name: "Fiji", capital: "Suva", language: "English, Fijian, Hindi" },
  { code: "FI", name: "Finland", capital: "Helsinki", language: "Finnish, Swedish" },
  { code: "FR", name: "France", capital: "Paris", language: "French" },
  { code: "GA", name: "Gabon", capital: "Libreville", language: "French" },
  { code: "GM", name: "Gambia", capital: "Banjul", language: "English" },
  { code: "GE", name: "Georgia", capital: "Tbilisi", language: "Georgian" },
  { code: "DE", name: "Germany", capital: "Berlin", language: "German" },
  { code: "GH", name: "Ghana", capital: "Accra", language: "English" },
  { code: "GR", name: "Greece", capital: "Athens", language: "Greek" },
  { code: "GD", name: "Grenada", capital: "St. George's", language: "English" },
  { code: "GT", name: "Guatemala", capital: "Guatemala City", language: "Spanish" },
  { code: "GN", name: "Guinea", capital: "Conakry", language: "French" },
  { code: "GW", name: "Guinea-Bissau", capital: "Bissau", language: "Portuguese" },
  { code: "GY", name: "Guyana", capital: "Georgetown", language: "English" },
  { code: "HT", name: "Haiti", capital: "Port-au-Prince", language: "French, Haitian Creole" },
  { code: "HN", name: "Honduras", capital: "Tegucigalpa", language: "Spanish" },
  { code: "HU", name: "Hungary", capital: "Budapest", language: "Hungarian" },
  { code: "IS", name: "Iceland", capital: "Reykjavik", language: "Icelandic" },
  { code: "IN", name: "India", capital: "New Delhi", language: "Hindi, English" },
  { code: "ID", name: "Indonesia", capital: "Jakarta", language: "Indonesian" },
  { code: "IR", name: "Iran", capital: "Tehran", language: "Persian" },
  { code: "IQ", name: "Iraq", capital: "Baghdad", language: "Arabic, Kurdish" },
  { code: "IE", name: "Ireland", capital: "Dublin", language: "Irish, English" },
  { code: "IL", name: "Israel", capital: "Jerusalem", language: "Hebrew, Arabic" },
  { code: "IT", name: "Italy", capital: "Rome", language: "Italian" },
  { code: "JM", name: "Jamaica", capital: "Kingston", language: "English" },
  { code: "JP", name: "Japan", capital: "Tokyo", language: "Japanese" },
  { code: "JO", name: "Jordan", capital: "Amman", language: "Arabic" },
  { code: "KZ", name: "Kazakhstan", capital: "Astana", language: "Kazakh, Russian" },
  { code: "KE", name: "Kenya", capital: "Nairobi", language: "Swahili, English" },
  { code: "KI", name: "Kiribati", capital: "Tarawa", language: "English, Gilbertese" },
  { code: "KP", name: "North Korea", capital: "Pyongyang", language: "Korean" },
  { code: "KR", name: "South Korea", capital: "Seoul", language: "Korean" },
  { code: "KW", name: "Kuwait", capital: "Kuwait City", language: "Arabic" },
  { code: "KG", name: "Kyrgyzstan", capital: "Bishkek", language: "Kyrgyz, Russian" },
  { code: "LA", name: "Laos", capital: "Vientiane", language: "Lao" },
  { code: "LV", name: "Latvia", capital: "Riga", language: "Latvian" },
  { code: "LB", name: "Lebanon", capital: "Beirut", language: "Arabic" },
  { code: "LS", name: "Lesotho", capital: "Maseru", language: "Sesotho, English" },
  { code: "LR", name: "Liberia", capital: "Monrovia", language: "English" },
  { code: "LY", name: "Libya", capital: "Tripoli", language: "Arabic" },
  { code: "LI", name: "Liechtenstein", capital: "Vaduz", language: "German" },
  { code: "LT", name: "Lithuania", capital: "Vilnius", language: "Lithuanian" },
  { code: "LU", name: "Luxembourg", capital: "Luxembourg", language: "Luxembourgish, French, German" },
  { code: "MG", name: "Madagascar", capital: "Antananarivo", language: "Malagasy, French" },
  { code: "MW", name: "Malawi", capital: "Lilongwe", language: "English, Chichewa" },
  { code: "MY", name: "Malaysia", capital: "Kuala Lumpur", language: "Malay" },
  { code: "MV", name: "Maldives", capital: "Malé", language: "Dhivehi" },
  { code: "ML", name: "Mali", capital: "Bamako", language: "French" },
  { code: "MT", name: "Malta", capital: "Valletta", language: "Maltese, English" },
  { code: "MH", name: "Marshall Islands", capital: "Majuro", language: "Marshallese, English" },
  { code: "MR", name: "Mauritania", capital: "Nouakchott", language: "Arabic" },
  { code: "MU", name: "Mauritius", capital: "Port Louis", language: "English" },
  { code: "MX", name: "Mexico", capital: "Mexico City", language: "Spanish" },
  { code: "FM", name: "Micronesia", capital: "Palikir", language: "English" },
  { code: "MD", name: "Moldova", capital: "Chișinău", language: "Romanian" },
  { code: "MC", name: "Monaco", capital: "Monaco", language: "French" },
  { code: "MN", name: "Mongolia", capital: "Ulaanbaatar", language: "Mongolian" },
  { code: "ME", name: "Montenegro", capital: "Podgorica", language: "Montenegrin" },
  { code: "MA", name: "Morocco", capital: "Rabat", language: "Arabic, Berber" },
  { code: "MZ", name: "Mozambique", capital: "Maputo", language: "Portuguese" },
  { code: "MM", name: "Myanmar", capital: "Naypyidaw", language: "Burmese" },
  { code: "NA", name: "Namibia", capital: "Windhoek", language: "English" },
  { code: "NR", name: "Nauru", capital: "Yaren", language: "Nauruan, English" },
  { code: "NP", name: "Nepal", capital: "Kathmandu", language: "Nepali" },
  { code: "NL", name: "Netherlands", capital: "Amsterdam", language: "Dutch" },
  { code: "NZ", name: "New Zealand", capital: "Wellington", language: "English, Māori" },
  { code: "NI", name: "Nicaragua", capital: "Managua", language: "Spanish" },
  { code: "NE", name: "Niger", capital: "Niamey", language: "French" },
  { code: "NG", name: "Nigeria", capital: "Abuja", language: "English" },
  { code: "MK", name: "North Macedonia", capital: "Skopje", language: "Macedonian" },
  { code: "NO", name: "Norway", capital: "Oslo", language: "Norwegian" },
  { code: "OM", name: "Oman", capital: "Muscat", language: "Arabic" },
  { code: "PK", name: "Pakistan", capital: "Islamabad", language: "Urdu, English" },
  { code: "PW", name: "Palau", capital: "Ngerulmud", language: "Palauan, English" },
  { code: "PS", name: "Palestine", capital: "Ramallah", language: "Arabic" },
  { code: "PA", name: "Panama", capital: "Panama City", language: "Spanish" },
  { code: "PG", name: "Papua New Guinea", capital: "Port Moresby", language: "English, Tok Pisin, Hiri Motu" },
  { code: "PY", name: "Paraguay", capital: "Asunción", language: "Spanish, Guarani" },
  { code: "PE", name: "Peru", capital: "Lima", language: "Spanish" },
  { code: "PH", name: "Philippines", capital: "Manila", language: "Filipino, English" },
  { code: "PL", name: "Poland", capital: "Warsaw", language: "Polish" },
  { code: "PT", name: "Portugal", capital: "Lisbon", language: "Portuguese" },
  { code: "QA", name: "Qatar", capital: "Doha", language: "Arabic" },
  { code: "RO", name: "Romania", capital: "Bucharest", language: "Romanian" },
  { code: "RU", name: "Russia", capital: "Moscow", language: "Russian" },
  { code: "RW", name: "Rwanda", capital: "Kigali", language: "Kinyarwanda, French, English" },
  { code: "KN", name: "Saint Kitts and Nevis", capital: "Basseterre", language: "English" },
  { code: "LC", name: "Saint Lucia", capital: "Castries", language: "English" },
  { code: "VC", name: "Saint Vincent", capital: "Kingstown", language: "English" },
  { code: "WS", name: "Samoa", capital: "Apia", language: "Samoan, English" },
  { code: "SM", name: "San Marino", capital: "San Marino", language: "Italian" },
  { code: "ST", name: "Sao Tome and Principe", capital: "São Tomé", language: "Portuguese" },
  { code: "SA", name: "Saudi Arabia", capital: "Riyadh", language: "Arabic" },
  { code: "SN", name: "Senegal", capital: "Dakar", language: "French" },
  { code: "RS", name: "Serbia", capital: "Belgrade", language: "Serbian" },
  { code: "SC", name: "Seychelles", capital: "Victoria", language: "Seychellois Creole, English, French" },
  { code: "SL", name: "Sierra Leone", capital: "Freetown", language: "English" },
  { code: "SG", name: "Singapore", capital: "Singapore", language: "English, Malay, Mandarin, Tamil" },
  { code: "SK", name: "Slovakia", capital: "Bratislava", language: "Slovak" },
  { code: "SI", name: "Slovenia", capital: "Ljubljana", language: "Slovene" },
  { code: "SB", name: "Solomon Islands", capital: "Honiara", language: "English" },
  { code: "SO", name: "Somalia", capital: "Mogadishu", language: "Somali, Arabic" },
  { code: "ZA", name: "South Africa", capital: "Pretoria", language: "11 official languages" },
  { code: "SS", name: "South Sudan", capital: "Juba", language: "English" },
  { code: "ES", name: "Spain", capital: "Madrid", language: "Spanish" },
  { code: "LK", name: "Sri Lanka", capital: "Colombo", language: "Sinhala, Tamil" },
  { code: "SD", name: "Sudan", capital: "Khartoum", language: "Arabic, English" },
  { code: "SR", name: "Suriname", capital: "Paramaribo", language: "Dutch" },
  { code: "SE", name: "Sweden", capital: "Stockholm", language: "Swedish" },
  { code: "CH", name: "Switzerland", capital: "Bern", language: "German, French, Italian, Romansh" },
  { code: "SY", name: "Syria", capital: "Damascus", language: "Arabic" },
  { code: "TW", name: "Taiwan", capital: "Taipei", language: "Mandarin" },
  { code: "TJ", name: "Tajikistan", capital: "Dushanbe", language: "Tajik" },
  { code: "TZ", name: "Tanzania", capital: "Dodoma", language: "Swahili, English" },
  { code: "TH", name: "Thailand", capital: "Bangkok", language: "Thai" },
  { code: "TL", name: "Timor-Leste", capital: "Dili", language: "Portuguese, Tetum" },
  { code: "TG", name: "Togo", capital: "Lomé", language: "French" },
  { code: "TO", name: "Tonga", capital: "Nukuʻalofa", language: "Tongan, English" },
  { code: "TT", name: "Trinidad and Tobago", capital: "Port of Spain", language: "English" },
  { code: "TN", name: "Tunisia", capital: "Tunis", language: "Arabic" },
  { code: "TR", name: "Turkey", capital: "Ankara", language: "Turkish" },
  { code: "TM", name: "Turkmenistan", capital: "Ashgabat", language: "Turkmen" },
  { code: "TV", name: "Tuvalu", capital: "Funafuti", language: "Tuvaluan, English" },
  { code: "UG", name: "Uganda", capital: "Kampala", language: "English, Swahili" },
  { code: "UA", name: "Ukraine", capital: "Kyiv", language: "Ukrainian" },
  { code: "AE", name: "United Arab Emirates", capital: "Abu Dhabi", language: "Arabic" },
  { code: "GB", name: "United Kingdom", capital: "London", language: "English" },
  { code: "US", name: "United States", capital: "Washington D.C.", language: "English" },
  { code: "UY", name: "Uruguay", capital: "Montevideo", language: "Spanish" },
  { code: "UZ", name: "Uzbekistan", capital: "Tashkent", language: "Uzbek" },
  { code: "VU", name: "Vanuatu", capital: "Port Vila", language: "Bislama, English, French" },
  { code: "VA", name: "Vatican City", capital: "Vatican City", language: "Italian, Latin" },
  { code: "VE", name: "Venezuela", capital: "Caracas", language: "Spanish" },
  { code: "VN", name: "Vietnam", capital: "Hanoi", language: "Vietnamese" },
  { code: "YE", name: "Yemen", capital: "Sana'a", language: "Arabic" },
  { code: "ZM", name: "Zambia", capital: "Lusaka", language: "English" },
  { code: "ZW", name: "Zimbabwe", capital: "Harare", language: "English, Shona, Ndebele" },
];

const CountriesPage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");

  // Filter countries based on search - prioritize names starting with query
  const filteredCountries = useMemo(() => {
    if (!searchQuery.trim()) {
      return ALL_COUNTRIES;
    }
    const query = searchQuery.toLowerCase().trim();
    
    // First: Countries whose name starts with the query
    const startsWithQuery = ALL_COUNTRIES.filter(
      (country) => country.name.toLowerCase().startsWith(query)
    );
    
    // Second: Countries where capital or language starts with query (but name doesn't)
    const otherMatches = ALL_COUNTRIES.filter(
      (country) =>
        !country.name.toLowerCase().startsWith(query) &&
        (country.capital.toLowerCase().startsWith(query) ||
         country.code.toLowerCase().startsWith(query))
    );
    
    // Combine: names first, then other matches
    return [...startsWithQuery, ...otherMatches];
  }, [searchQuery]);

  return (
    <div className="page-container p-4 pb-28" data-testid="countries-page">
      <SEO title="Countries & Visa Checker" description="Check visa requirements for 199 passports. Find travel info, weather, and points of interest for any country." path="/countries" />
      {/* Hero Banner */}
      <div className="relative h-32 sm:h-40 lg:h-52 overflow-hidden -mx-4 -mt-0 mb-6 rounded-b-2xl">
        <img src="https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1200&q=80" alt="" className="w-full h-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#080d19]/80 via-[#080d19]/20 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-4 pb-5 lg:p-6 lg:pb-7">
          <h1 className="text-2xl lg:text-3xl font-bold font-heading text-white drop-shadow-lg">Country & Visa</h1>
          <p className="text-sm lg:text-base text-slate-300/80 mt-0.5 drop-shadow">Select a destination</p>
        </div>
      </div>

      {/* Search Bar */}
      <div className="relative mb-6">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
        <Input
          type="text"
          placeholder="Search countries, capitals, languages..."
          className="w-full pl-12 pr-4 py-3 bg-slate-900/70 border border-slate-700/50 rounded-xl text-white placeholder:text-slate-500 focus:border-orange-500/50 focus:ring-orange-500/20"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          data-testid="country-search"
        />
      </div>

      {/* Results count */}
      <p className="text-sm text-slate-500 mb-4">
        {filteredCountries.length} countries found
      </p>

      {/* Countries List - 3 columns on desktop */}
      <ScrollArea className="h-[calc(100vh-260px)]">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 pr-2">
          {filteredCountries.map((country) => (
            <button
              key={country.code}
              onClick={() => navigate(`/country/${country.code}`)}
              className="w-full relative overflow-hidden flex items-center gap-4 p-4 rounded-xl hover:scale-[1.01] transition-all"
              data-testid={`country-${country.code.toLowerCase()}`}
            >
              <img
                src={getCountryPhotoByCode(country.code)}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0f172a]/80 via-[#0f172a]/50 to-[#0f172a]/30" />
              <div className="relative w-12 h-12 lg:w-14 lg:h-14 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center flex-shrink-0 overflow-hidden border border-white/20">
                <img
                  src={`https://flagcdn.com/w80/${country.code.toLowerCase()}.png`}
                  alt={`${country.name} flag`}
                  className="w-10 h-6 lg:w-11 lg:h-7 rounded object-cover shadow-sm"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
              <div className="relative flex-1 text-left min-w-0">
                <h3 className="text-base lg:text-lg font-semibold text-white truncate drop-shadow">
                  {country.name}
                </h3>
                <div className="flex items-center gap-3 mt-1 text-sm lg:text-base text-slate-200/80">
                  <span className="flex items-center gap-1 truncate drop-shadow">
                    <MapPin className="w-3 h-3 flex-shrink-0" />
                    {country.capital}
                  </span>
                </div>
              </div>
              <ChevronRight className="relative w-5 h-5 lg:w-5 lg:h-5 text-white/60 flex-shrink-0" />
            </button>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
};

export default CountriesPage;
